import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";

import { STATUS_CONFIG, DEFAULT_STATUS } from "./status-config";
import { formatKPIValue } from "./helper";

import type { FC } from "react";
import type { GaugeChartProps } from "./types";

const KpiRow: FC<{ label: string; value?: number }> = ({
  label = "",
  value = 0
}) => (
  <div className="flex justify-between w-full h-8">
    <p className="font-medium text-xs leading-6 text-neutral-700">{label}</p>

    <span dir="ltr" className="font-medium text-sm leading-6 text-neutral-700">
      {formatKPIValue({ value })}
    </span>
  </div>
);

const GaugeChart: FC<GaugeChartProps> = ({
  data: _data = [],
  centerText = "عالی",
  centerNum = 90.91,
  showLabels = true,
  className = "",
  activeIndex = -1,
  onSegmentClick,
  grainName: _grainName,
  grainType: _grainType,
  grainId: _grainId,
  mark,
  markWN,
  accuracyRate,
  corrects = 0,
  inCorrects = 0,
  noAnswers = 0,
  performanceLabel,
  answerRate
}) => {
  const getStatus = (label: string) => STATUS_CONFIG[label] || DEFAULT_STATUS;

  const total = (corrects ?? 0) + (inCorrects ?? 0) + (noAnswers ?? 0);

  const computedData = useMemo(
    () => [
      {
        name: "درست",
        value: corrects,
        color: "#10B981",
        percentage: (corrects / total) * 100
      },
      {
        name: "غلط",
        value: inCorrects,
        color: "#EF4444",
        percentage: (inCorrects / total) * 100
      },
      {
        name: "نزده",
        value: noAnswers,
        color: "#9CA3AF",
        percentage: (noAnswers / total) * 100
      }
    ],
    [corrects, inCorrects, noAnswers, total]
  );

  // Use exam data for center values if provided
  const finalCenterNum = mark ?? centerNum;
  const finalCenterText = centerText ?? centerText;

  // Prepare chart data with calculated angles
  const chartData = useMemo(() => {
    let cumulativeValue = 0;
    return computedData.map((item) => {
      const startAngle = 180 - (cumulativeValue / total) * 180;
      cumulativeValue += item.value;
      const endAngle = 180 - (cumulativeValue / total) * 180;

      return {
        ...item,
        startAngle,
        endAngle,
        percentage: ((item.value / total) * 100).toFixed(1)
      };
    });
  }, [computedData, total]);

  const renderActiveShape = (props: unknown) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
      (props as any) || {};

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  // Handle segment click
  const handleClick = (
    data: { name: string; value: number; color: string; percentage: string },
    _index: number
  ) => {
    if (onSegmentClick) onSegmentClick(data);
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="bg-[#FFF9F5] border border-[#FF6A00] rounded-2 py-4 px-7 ">
        <div className="w-full relative">
          <ResponsiveContainer height={250} minWidth={183}>
            <PieChart>
              <Pie
                data={computedData}
                cx="50%"
                cy="50%"
                // width="100%"
                startAngle={180}
                endAngle={0}
                innerRadius="60%"
                outerRadius="80%"
                cornerRadius={2}
                paddingAngle={0}
                dataKey="value"
                onClick={handleClick}
                activeIndex={activeIndex}
                activeShape={activeIndex >= 0 ? renderActiveShape : undefined}
              >
                {computedData.map((entry, index) => (
                  <Cell
                    key={`${entry.name}-cell-${index}`}
                    fill={entry.color}
                    className="cursor-pointer transition-opacity hover:opacity-80"
                  />
                ))}
              </Pie>

              <text
                x="45%"
                y="49%"
                textAnchor="middle"
                dominantBaseline="middle"
                transform="translate(-90, 0)"
                className="text-xs font-medium fill-neutral-600"
              >
                0
              </text>

              {/* 100% label at end angle (0 degrees) */}
              <text
                x="56%"
                y="49%"
                textAnchor="middle"
                dominantBaseline="middle"
                transform="translate(90, 0)"
                className="text-xs font-medium fill-neutral-600"
              >
                100
              </text>
            </PieChart>
          </ResponsiveContainer>

          {/* Center text display */}
          {!!finalCenterNum && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none -top-11">
              <div className="text-center ">
                <p
                  dir="ltr"
                  className="text-sm leading-8 font-medium text-neutral-700"
                  style={{
                    color: getStatus(performanceLabel ?? "نامشخص").color
                  }}
                >
                  {`${finalCenterNum} %`}
                </p>

                {finalCenterText && (
                  <span className="text-neutral-700 font-medium text-xs">
                    {finalCenterText}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Labels section */}
        {showLabels && (
          <div className="flex justify-between w-full -mt-22">
            {chartData.map((item, index) => (
              <div
                key={`label-${item.name}-${index}`}
                className="flex items-center gap-1 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-[#8C97A7]">{item.name}</span>
                </div>

                <span dir="ltr" className="text-xs font-medium text-[#8C97A7]">
                  {item.percentage}%
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          className="mx-auto mt-5 border w-fit px-5 h-6 flex items-center justify-center text-xs font-medium rounded-3 transition-colors relative overflow-hidden"
          style={{
            color: getStatus(performanceLabel ?? "نامشخص").color,
            backgroundColor: getStatus(performanceLabel ?? "نامشخص")
              .backgroundColor,
            borderColor: getStatus(performanceLabel ?? "نامشخص").color
          }}
        >
          {getStatus(performanceLabel ?? "نامشخص").text}
        </button>
      </div>

      <div className="w-full mt-4 flex flex-col justify-between gap-3 px-2">
        <KpiRow label="نمره (بدون نمره منفی):" value={markWN} />
        <KpiRow label="نرخ پاسخ‌دهی:" value={answerRate} />
        <KpiRow label="دقت پاسخ‌دهی:" value={accuracyRate} />
      </div>
    </div>
  );
};

export default GaugeChart;
